/* eslint-disable no-useless-escape */
// Application-specific utilities
// ------------------------------
import LocaleService from 'services/loader-handler-services/locale-service';
import Chaplin from 'chaplin';
import { marked } from 'marked';
import _ from 'lodash';
import EF from 'ef/ef';
import $ from 'jquery';
import Handlebars from 'handlebars/runtime';
import Backbone from 'backbone';
import AlertService from 'services/course-handler-services/alert-service';

// Delegate to Chaplin’s utils module
const utils = Chaplin.utils.beget(Chaplin.utils);

// Add additional application-specific properties and methods
_.extend(utils, {
  initialize: function (options) {
    options = options || {};
    this.options = _.defaults(options, {
      courseUrl: '',
    });
  },
  redirectToPage: function (page) {
    Chaplin.utils.redirectTo({ url: page.path() });
  },
  redirectToModule: function (module) {
    Chaplin.utils.redirectTo({ url: module.get('key') });
  },
  tryRequire: function (path) {
    try {
      return require(path);
    } catch (e) {
      if (EF.options.local) {
        //console.trace();
        if (e.message.indexOf('Cannot find module') === -1) {
          // eslint-disable-next-line no-console
          console.error(`Can't load ${path}: ${e.message}`);
        } else {
          // eslint-disable-next-line no-console
          console.debug(`Can't load ${path}, but that's probably OK`);
        }
      }
      return undefined;
    }
  },
  dotStringToObject: function (dotNotation, object) {
    return dotNotation.split('.').reduce((obj, i) => {
      if (obj) {
        return obj[i];
      }
    }, object);
  },
  getByDotString: function (dotString, getter) {
    const rawData = dotString.split('.');
    const key = rawData.shift();
    const obj = getter(key);

    if (obj) {
      return rawData.length === 0 ? obj : utils.dotStringToObject(rawData.join('.'), obj);
    }
  },
  tryParseJson: function (value) {
    let result;
    try {
      result = JSON.parse(value);
    } catch (e) {
      result = value;
    }
    return result;
  },
  coursePath: function (path) {
    if (path.substring(0, 4) === 'http' || path.substring(0, 2) === '//') {
      return path;
    } else if (path.substring(0, 1) === '/') {
      path = path.substring(1, path.length);
    }
    path = path.replace('{{lang}}', LocaleService.getLanguage());
    return (this.options ? this.options.courseUrl : '') + path;
  },

  // Cache bust a path. Assumes path has no parameters.
  bust: function (path) {
    return `${path}?_=${Date.now()}`;
  },
  isEmpty: function (obj) {
    if (typeof obj === 'undefined') {
      return true;
    }
    return Object.keys(obj).length === 0;
  },
  renderMarkdown(value) {
    return marked.parse(value);
  },
  slugify(text) {
    return text
      .toString()
      .toLowerCase()
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(/[^\w\-]+/g, '') // Remove all non-word chars
      .replace(/\-\-+/g, '-') // Replace multiple - with single -
      .replace(/^-+/, '') // Trim - from start of text
      .replace(/-+$/, ''); // Trim - from end of text
  },
  treatAsUTC(date) {
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date;
  },
  daysFromToday(startDate) {
    if (startDate.includes('-')) {
      startDate = startDate.split('-').join('/');
    }
    return this.daysDiff(startDate, new Date());
  },
  daysDiff(startDate, endDate) {
    startDate = startDate instanceof Date ? startDate : new Date(startDate);
    endDate = endDate instanceof Date ? endDate : new Date(endDate);

    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    return (this.treatAsUTC(endDate) - this.treatAsUTC(startDate)) / millisecondsPerDay;
  },
  toMinutesSeconds(inputSeconds) {
    const secs = parseInt(inputSeconds, 10);
    const minutes = Math.floor(secs / 60);
    const seconds = secs - minutes * 60;
    if (minutes > 0) {
      return `${minutes} ${LocaleService.t('video.minutes')} ${seconds} ${LocaleService.t('video.seconds')}`;
    }
    return `${seconds} ${LocaleService.t('video.seconds')}`;
  },
  sanifyJsonApiObject(insaneObject, includes) {
    if (!insaneObject) {
      return;
    }
    if (Array.isArray(insaneObject)) {
      return insaneObject.map(obj => this.sanifyJsonApiObject(obj, includes));
    }
    if (!insaneObject.attributes && includes) {
      const includedObject = includes.find(incl => {
        return incl.id === insaneObject.id && incl.type === insaneObject.type;
      });
      if (!includedObject) {
        return insaneObject;
      } else {
        insaneObject = includedObject;
      }
    }
    const saneObject = Object.assign({}, insaneObject, insaneObject.attributes);
    delete saneObject.attributes;
    if (saneObject.relationships) {
      Object.entries(saneObject.relationships).forEach(([key, value]) => {
        if (value.data) {
          if (Array.isArray(value.data)) {
            saneObject[key] = [];
            value.data.forEach(val => saneObject[key].push(this.sanifyJsonApiObject(val, includes)));
          } else {
            saneObject[key] = this.sanifyJsonApiObject(value.data, includes);
          }
        }
      });
      delete saneObject.relationships;
    }
    return saneObject;
  },
  failProofPromise(promise, failResult = {}) {
    return promise.then(
      data => data,
      () => $.Deferred().resolve(failResult).promise()
    );
  },
  parentElements(element, stepUp) {
    let elementGrandParent = element;
    while (stepUp > 0) {
      elementGrandParent = elementGrandParent.parentElement;
      stepUp--;
    }
    return elementGrandParent;
  },
  focusableElements() {
    return 'a[href]:not([tabindex="-1"]):not([disabled]),area[href]:not([tabindex="-1"]),input:not([disabled]):not([tabindex="-1"]),select:not([disabled]):not([tabindex="-1"]),textarea:not([disabled]):not([tabindex="-1"]),button:not([disabled]):not([tabindex="-1"]),iframe:not([tabindex="-1"]),[tabindex]:not([tabindex="-1"]),[contentEditable=true]:not([tabindex="-1"])';
  },
  isDevEnv() {
    return EF.config.get('env') === 'dev' && window != null;
  },
  devLog(msg, lvl = 'debug') {
    if (utils.isDevEnv()) {
      console[lvl](msg);
    }
  },
  getLastArrayElement(array) {
    return array[array.length - 1];
  },
  createSafeString(safeString, unsafeString) {
    return new Handlebars.SafeString(safeString + Handlebars.Utils.escapeExpression(unsafeString));
  },
  getMediaBasePath(options, courseSettingsService) {
    let basePath;
    if (EF.options.local) {
      if (options.mediaType === 'audio') {
        basePath = courseSettingsService.get('media_host');
      } else {
        basePath = options.customMediaHost
          ? courseSettingsService.get('custom_media_host')
          : courseSettingsService.get('media_host');
      }
    } else {
      try {
        const url = new URL(
          options.customMediaHost
            ? options.metadataUrl
            : courseSettingsService.get('media_host') ?? utils.coursePath(''),
          location
        );
        basePath = url.protocol.concat('//', url.hostname);
      } catch (error) {
        console.log('Something went wrong with the base path', error);
      }
    }
    return basePath.replace(/\/$/, '');
  },
  renderDataAttributes(selector, dataset = {}) {
    const el = document.querySelector(selector);
    if (el) {
      Object.keys(dataset).forEach(key => {
        el.dataset[key] = dataset[key];
      });
    }
  },
  watchAttributeChanges(element, attrs, callback) {
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes') {
          callback(mutation);
        }
      });
    });
    observer.observe(element, {
      attributeFilter: [...attrs],
      attributeOldValue: true,
      subtree: true,
    });
  },
  setHeadingLevel(model, headingLevel, defaultLevel) {
    model?.set('heading_level', headingLevel ?? defaultLevel ?? 'h2');
  },
  getPlatformHeaderHeight() {
    return document.querySelector('#site-container')?.getBoundingClientRect()?.top + window.scrollY;
  },
  // Grabbed this from the view helpers
  math: function (lvalue, operator, rvalue) {
    lvalue = parseFloat(lvalue);
    rvalue = parseFloat(rvalue);

    return {
      '+': lvalue + rvalue,
      '-': lvalue - rvalue,
      '*': lvalue * rvalue,
      '/': lvalue / rvalue,
      '%': lvalue % rvalue,
    }[operator];
  },
  pixelMath(pixel1, operation, pixel2) {
    let pixel1Val = 0,
      pixel2Val = 0;
    if (typeof pixel1 === 'string') {
      pixel1Val = parseInt(pixel1.replace(/px/, ''));
    } else if (typeof pixel1 === 'number') {
      pixel1Val = parseInt(pixel1);
    }

    if (typeof pixel2 === 'string') {
      pixel2Val = parseInt(pixel2.replace(/px/, ''));
    } else if (typeof pixel2 === 'number') {
      pixel2Val = parseInt(pixel2);
    }
    return `${this.math(pixel1Val, operation, pixel2Val)}px`;
  },
  subAriaHiddenOnDialog(_this, selector) {
    _this.subscribeEvent('dialog:show', () => {
      $(selector).attr('aria-hidden', true);
    });
    _this.subscribeEvent('dialog:hide', () => {
      $(selector).attr('aria-hidden', false);
    });
  },
  backboneSync(_this, _args) {
    //if in local mode just do nothing
    if (EF.options.local && !EF.options.loadCustomizationsRemote) {
      return;
    }
    return Backbone.sync.apply(_this, _args);
  },
  getEnrollmentsUrl(enrollmentId, fileName) {
    return EF.url(`/sdk/enrollments/${enrollmentId}/${fileName}`);
  },
  /**
   *
   * @param {*} url url that we want to send request to
   * @param {*} params params for the request
   * @param {*} retry params (count, callback)
   * @returns
   */
  async fetch(url, params, { count = 0, callback = (url, params) => ({ newUrl: url, newParams: params }) }) {
    try {
      const response = await fetch(url, params);
      if (response.status !== 200) {
        throw response;
      }
      return response;
    } catch (error) {
      if (count <= 0) {
        throw error;
      }
      const { newUrl, newParams } = callback(url, params, { count, callback });
      count--;
      return await this.fetch(newUrl, newParams, { count, callback });
    }
  },
  applyAnimationProperty: function (_this) {
    if (_this.model.get('animation')) {
      _this.$el.attr('data-anijs', _this.model.get('animation'));
    }

    if (!_this.model.get('onscroll')) {
      return;
    }
    const scrollParams = _this.model.get('onscroll');
    for (const prop in scrollParams) {
      if (!Object.prototype.hasOwnProperty.call(scrollParams, prop)) {
        continue;
      }
      _this.$el.attr(prop === 'animation' ? 'data-aos' : `data-aos-${prop}`, scrollParams[prop]);
    }
  },
  //[TODO]: move this to error handling service one refactored
  httpErrorHandler: function (response, showSuccess = true) {
    if (EF.options.local || (response.status >= 200 && response.status < 300)) {
      if (showSuccess) {
        AlertService.showSuccessAlert();
      }
      return Promise.resolve(response);
    } else {
      AlertService.showFailureAlert();
      return Promise.reject(new Error(`${response.status} : ${response.statusText}`));
    }
  },
  setSectionsData: function (data) {
    data.sections = _.orderBy(data.sections, 'position');
    data.sections.forEach(section => {
      section.questions = _.orderBy(section.questions, 'position');
      section.questions.forEach(question => {
        question.answers = _.orderBy(question.answers, 'position');
      });
    });
  },
  loadPageSRText: function (title) {
    const pageTitle = title ? title : '';
    requestAnimationFrame(() => {
      $('#page-loading').attr('aria-hidden', false);
      $('#page-loading').text(`${LocaleService.t('defaultPageTitle')} ${LocaleService.t(pageTitle)}`);
    });
  },
  isNumeric: function (n) {
    return !isNaN(parseFloat(n)) && isFinite(n);
  },
});

export function isFloat(n) {
  if (typeof n !== 'number') {
    throw Error('isFloat Error: param should be number type');
  }
  return Number(n) === n && n % 1 !== 0;
}
export default utils;
